const byCreatedAtDesc = (a, b) => new Date(b.created_at) - new Date(a.created_at);
const byCreatedAtAsc = (a, b) => new Date(a.created_at) - new Date(b.created_at);

/**
 * Groups branches under their parent company.
 * - matches: processes matching the current search (all visible ones without search)
 * - visible: processes allowed by the "My companies / All" filter
 * - all: every process, only used to name a parent that is not in the list
 */
export function buildCompanyTree({ matches, visible, all, isSearching }) {
  const visibleByCompanyId = new Map(
    visible.filter((p) => p.company_id).map((p) => [p.company_id, p])
  );
  const parentOf = (p) =>
    p.parent_company_id ? visibleByCompanyId.get(p.parent_company_id) : null;

  const topOf = (p) => {
    const seen = new Set([p.id]);
    let current = p;
    let parent = parentOf(current);
    while (parent && !seen.has(parent.id)) {
      seen.add(parent.id);
      current = parent;
      parent = parentOf(current);
    }
    return current;
  };

  const descendantsOf = (root) => {
    const found = [];
    const seen = new Set([root.id]);
    const queue = [root];
    while (queue.length > 0) {
      const current = queue.shift();
      visible.forEach((candidate) => {
        if (
          candidate.parent_company_id &&
          candidate.parent_company_id === current.company_id &&
          !seen.has(candidate.id)
        ) {
          seen.add(candidate.id);
          found.push(candidate);
          queue.push(candidate);
        }
      });
    }
    return found.sort(byCreatedAtAsc);
  };

  const matchedIds = new Set(matches.map((p) => p.id));
  const dimmed = (p) => isSearching && !matchedIds.has(p.id);

  const roots = new Map();
  matches.forEach((p) => {
    const root = topOf(p);
    roots.set(root.id, root);
  });

  return Array.from(roots.values())
    .sort(byCreatedAtDesc)
    .map((root) => {
      let parentName = null;
      if (root.parent_company_id) {
        const parent = all.find((p) => p.company_id === root.parent_company_id);
        parentName = parent ? parent.company_name : "another company";
      }
      return {
        item: root,
        parentName,
        isDimmed: dimmed(root),
        branches: descendantsOf(root).map((branch) => ({
          item: branch,
          isDimmed: dimmed(branch),
        })),
      };
    });
}
